import { Grid, Typography, makeStyles } from '@material-ui/core';
import { observer } from 'mobx-react';
import { Button, StandardDialog } from '@roc/ui';
import { useStore } from '@roc/client-portal-shared/hooks';
import { Document } from '@roc/feature-types';
import {
  OPERATING_AGREEMENT,
  OPERATING_AGREEMENT_BYLAWS_STOCK,
  CERTIFICATE_OF_FORMATION,
  ARTICLES_OF_INCORPORATION,
  CERT_OF_FORMATION_OR_INC,
} from '@roc/feature-documents';
import { DocReviewEditContentResolver } from './docReviewEditContentResolver';

const useStyles = makeStyles({
  root: {
    padding: 0,
  },
});

interface DocReviewEditLayout {
  title?: string;
  getSubtitle?: (docReviewStoreV2: any) => string | undefined;
}

const entityNameSubtitle = (docReviewStoreV2: any) =>
  docReviewStoreV2.entityReviewFormStore.entityName;

const OWNERSHIP_EDIT_LAYOUT: DocReviewEditLayout = {
  title: 'Edit Ownership',
  getSubtitle: entityNameSubtitle,
};

const FORMATION_EDIT_LAYOUT: DocReviewEditLayout = {
  getSubtitle: entityNameSubtitle,
};

const editLayoutFor = (documentName: string): DocReviewEditLayout =>
  ({
    [OPERATING_AGREEMENT]: OWNERSHIP_EDIT_LAYOUT,
    [OPERATING_AGREEMENT_BYLAWS_STOCK]: OWNERSHIP_EDIT_LAYOUT,
    [CERTIFICATE_OF_FORMATION]: FORMATION_EDIT_LAYOUT,
    [ARTICLES_OF_INCORPORATION]: FORMATION_EDIT_LAYOUT,
    [CERT_OF_FORMATION_OR_INC]: FORMATION_EDIT_LAYOUT,
  }[documentName] ?? {});

interface DocReviewEditModalProps {
  open: boolean;
  onClose: () => void;
  document: Document;
}

export const DocReviewEditModal = observer(({
  open,
  onClose,
  document,
}: DocReviewEditModalProps) => {
  const classes = useStyles();
  const { docReviewStoreV2 } = useStore();
  const documentName = document.taskName ?? '';
  const { title, getSubtitle } = editLayoutFor(documentName);
  const subtitle = getSubtitle?.(docReviewStoreV2);

  const handleSave = async () => {
    await docReviewStoreV2.saveDocReviewTaskData(document);
    onClose();
  };

  const dialogActions = (
    <Grid container justifyContent="flex-end" spacing={2}>
      <Grid item>
        <Button onClick={onClose} variant="outlined" color="primary" testId="cancel">
          Cancel
        </Button>
      </Grid>
      <Grid item>
        <Button onClick={handleSave} variant="contained" color="primary" testId="save">
          Save
        </Button>
      </Grid>
    </Grid>
  );

  return (
    <StandardDialog
      open={open}
      title={title ?? `Edit ${documentName}`}
      subTitle={
        subtitle ? (
          <Typography variant="body2" color="textSecondary">
            {subtitle}
          </Typography>
        ) : undefined
      }
      maxWidth="sm"
      handleClose={onClose}
      dialogContentclasses={{ root: classes.root }}
      dialogContent={
        <DocReviewEditContentResolver itemName={documentName} open={open} />
      }
      dialogActions={dialogActions}
    />
  );
});
